import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Lock, X, Delete } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Spinner } from "./common";

// Reusable PIN entry modal. Renders exactly the number of boxes the user chose
// (4 or 6), auto-submits when full, then calls /security/pin/verify and onSuccess().
export default function PinModal({ open, onClose, onSuccess, title = "Enter your PIN", subtitle = "Confirm it's you before paying" }) {
  const [pin, setPin] = useState("");
  const [length, setLength] = useState(null); // null = legacy flexible (4-6)
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) { setPin(""); return; }
    setPin(""); setLoading(true);
    api.get("/security/pin")
      .then(({ data }) => setLength(data.pin_length || null))
      .catch(() => setLength(null))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const dots = length || 6;
  const minLen = length || 4;

  const doVerify = async (code) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      await api.post("/security/pin/verify", { pin: code });
      setPin("");
      onSuccess();
    } catch (e) { toast.error(errMsg(e)); setPin(""); }
    finally { setBusy(false); submittingRef.current = false; }
  };

  const press = (d) => {
    if (busy) return;
    const next = (pin + d).slice(0, dots);
    setPin(next);
    if (length && next.length === length) doVerify(next);
  };
  const back = () => setPin(pin.slice(0, -1));
  const submit = () => { if (pin.length >= minLen) doVerify(pin); };

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50" onClick={onClose} data-testid="pin-modal">
      <div className="w-full max-w-md bg-card rounded-t-3xl p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary"><Lock size={18} /></span>
            <div>
              <h3 className="font-semibold">{title}</h3>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="pin-modal-close" className="text-muted-foreground"><X size={20} /></button>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center" data-testid="pin-modal-loading">
            <Spinner className="w-6 h-6 text-primary" />
          </div>
        ) : (
          <>
            <div className="flex justify-center gap-3 my-6">
              {Array.from({ length: dots }).map((_, i) => (
                <span key={i} className={`w-3.5 h-3.5 rounded-full transition-colors ${i < pin.length ? "bg-primary" : "bg-secondary"}`} />
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} onClick={() => press(String(n))} data-testid={`pin-key-${n}`}
                  className="h-14 rounded-2xl bg-secondary text-xl font-semibold transition-transform active:scale-95">{n}</button>
              ))}
              <button onClick={onClose} className="h-14 rounded-2xl text-sm text-muted-foreground font-semibold">Cancel</button>
              <button onClick={() => press("0")} data-testid="pin-key-0" className="h-14 rounded-2xl bg-secondary text-xl font-semibold transition-transform active:scale-95">0</button>
              <button onClick={back} data-testid="pin-key-back" className="h-14 rounded-2xl grid place-items-center text-muted-foreground transition-transform active:scale-95"><Delete size={22} /></button>
            </div>

            {!length && (
              <button onClick={submit} disabled={busy || pin.length < minLen} data-testid="pin-submit"
                className="w-full h-14 mt-5 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center disabled:opacity-50">
                {busy ? <Spinner className="w-5 h-5" /> : "Confirm"}
              </button>
            )}
            {length && busy && <div className="mt-5 flex justify-center"><Spinner className="w-5 h-5 text-primary" /></div>}
          </>
        )}
      </div>
    </div>
  );
}
